"use client";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { getBlogPageType } from "@/utils/blogUtils";
import CategoryPageSkeleton from "./CategoryPageSkeleton";
import SubcategoryPageSkeleton from "./SubcategoryPageSkeleton";
import PostPageSkeleton from "./PostPageSkeleton";
import BlogsListSkeleton from "./BlogsListSkeleton";
import BlogHeroSkeleton from "./BlogHeroSkeleton";

interface SmartLoadingProps {
  fallback?: React.ReactNode;
}

const SmartLoading: React.FC<SmartLoadingProps> = ({
  fallback = (
    <>
      <BlogHeroSkeleton />
      <BlogsListSkeleton />
    </>
  ),
}) => {
  const pathname = usePathname();
  const [loadingType, setLoadingType] = useState<
    "category" | "subcategory" | "post" | "default"
  >("default");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const determineLoadingType = async () => {
      const slug = pathname.replace("/blog/", "") || "";
      if (!slug) {
        setLoadingType("default");
        setIsLoading(false);
        return;
      }

      try {
        // Use the same API call that the actual page uses
        const blogPageType = await getBlogPageType(slug);

        if (blogPageType?.data?.type) {
          switch (blogPageType.data.type) {
            case "category":
              setLoadingType("category");
              break;
            case "subcategory":
              setLoadingType("subcategory");
              break;
            case "post":
              setLoadingType("post");
              break;
            default:
              setLoadingType("default");
          }
        } else {
          // Fallback to heuristic detection if API fails
          const slugLength = slug.length;
          const hasHyphens = slug.includes("-");
          const hasNumbers = /\d/.test(slug);

          if (
            slugLength > 20 ||
            (hasHyphens && slugLength > 15) ||
            hasNumbers
          ) {
            setLoadingType("post");
          } else {
            setLoadingType("category");
          }
        }
      } catch (error) {
        console.error("Error determining loading type:", error);
        // Fallback to heuristic detection
        const slugLength = slug.length;
        const hasHyphens = slug.includes("-");
        const hasNumbers = /\d/.test(slug);

        if (slugLength > 20 || (hasHyphens && slugLength > 15) || hasNumbers) {
          setLoadingType("post");
        } else {
          setLoadingType("category");
        }
      } finally {
        setIsLoading(false);
      }
    };

    determineLoadingType();
  }, [pathname]);

  // Show a generic loading state while determining the type
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-8 pb-32 pt-20 lg:pt-24">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <div className="h-2 w-2 animate-bounce rounded-full bg-gray-400"></div>
              <div
                className="h-2 w-2 animate-bounce rounded-full bg-gray-400"
                style={{ animationDelay: "0.1s" }}
              ></div>
              <div
                className="h-2 w-2 animate-bounce rounded-full bg-gray-400"
                style={{ animationDelay: "0.2s" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  switch (loadingType) {
    case "category":
      return <CategoryPageSkeleton />;
    case "subcategory":
      return <SubcategoryPageSkeleton />;
    case "post":
      return <PostPageSkeleton />;
    default:
      return <>{fallback}</>;
  }
};

export default SmartLoading;
